import { useEffect, type RefObject } from 'react';

/** Finds the nearest scrollable ancestor of `root` (the page scroller), or window. */
function getPageScroller(root: HTMLElement): HTMLElement | Window {
  let el: HTMLElement | null = root.parentElement;
  while (el) {
    const style = window.getComputedStyle(el);
    const oy = style.overflowY;
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
    el = el.parentElement;
  }
  return window;
}

/**
 * Makes a container "page-scroll first" for vertical mouse-wheel / trackpad input.
 *
 * Problem: on macOS especially, a vertical scroll gesture that lands on a
 * horizontally-scrollable row (e.g. the report-card rows) or a boundary-trapped
 * inner panel gets swallowed by that element, killing page-scroll momentum.
 *
 * This handler redirects a vertical-dominant wheel to the page scroller UNLESS
 * the gesture is over an inner vertical scroller that can still move in that
 * direction (so genuine lists like Invoices / Actions Required still scroll).
 * Horizontal-dominant gestures are left untouched so kanban rows still pan.
 */
export function usePageScrollPassthrough(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return; // pinch-zoom
      const dy = e.deltaY;
      if (!dy || Math.abs(e.deltaX) > Math.abs(dy)) return; // ignore horizontal-intent gestures

      // Let an inner vertical scroller handle it if it can still move that way.
      let node: HTMLElement | null = e.target as HTMLElement | null;
      while (node && node !== root) {
        if (node.nodeType === 1) {
          const style = window.getComputedStyle(node);
          const oy = style.overflowY;
          const scrollable =
            (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
            node.scrollHeight > node.clientHeight + 1;
          if (scrollable) {
            const atTop = node.scrollTop <= 0;
            const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
            if (dy < 0 ? !atTop : !atBottom) return; // inner list will consume the scroll
          }
        }
        node = node.parentElement;
      }

      // Nothing inner can consume it → drive the page scroller directly.
      const scroller = getPageScroller(root);
      if (scroller === window) {
        window.scrollBy(0, dy);
      } else {
        (scroller as HTMLElement).scrollTop += dy;
      }
      e.preventDefault();
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    return () => root.removeEventListener('wheel', onWheel);
  }, [ref]);
}
